import axios from "axios";
import { API_URL } from "src/scenes/App";

class SubscriptionPlan {
    constructor(data) {
        this.name = data.name;
        this.frequencyName = data.frequencyName;
        this.currency = data.currencyCode;
        this.taxFraction = data.taxFraction;
        this.periodNetAmountCents = data.periodNetAmountCents;
        this.durationNumber = data.durationNumber;
        this.durationUnit = data.durationUnit;
        this.paymentPeriod = data.paymentPeriod; // May be null
        this.periodsDue = data.periodsDue;
    }

    getUpfrontPeriods() {
        return this.paymentPeriod === null ? this.durationNumber : this.paymentPeriod;
    }

    /**
     * Return something more conformant to CompanySubscription from the API
     */
    withStartDate(startAt) {
        const formatter = Intl.DateTimeFormat("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
        const formattedStartDate = formatter.format(startAt);
        return {
            startAt: formattedStartDate,
            ...this
        };
    }

    /**
     * Get the tax-exclusive price in dollars per user over the entire subscription plan
     * @returns {number}
     */
    getSubtotalPerUser() {
        return this.getUpfrontPeriods() * (this.periodNetAmountCents / 100);
    }

    /**
     * Get the tax-exclusive price in dollars for a given number of users over the entire subscription plan
     * @param {number} numberOfUsers
     * @returns {number}
     */
    getSubtotal(numberOfUsers) {
        return numberOfUsers * this.getSubtotalPerUser();
    }

    /**
     *
     * @param {number} numberOfUsers
     * @returns {number}
     */
    getGst(numberOfUsers) {
        const subtotal = this.getSubtotal(numberOfUsers);
        return subtotal * this.taxFraction;
    }

    getDurationUnitLowercase() {
        return this.durationUnit.toLowerCase();
    }

    getPaymentDescription() {
        return this.frequencyName || "";
    }

    getDurationString() {
        return this.frequencyName || "";
    }

    getSlashSuffix() {
        if (this.durationUnit === "month") {
            if (this.paymentPeriod === null && this.durationUnit === 12) {
                return "yr";
            }
            if (this.paymentPeriod === 1) {
                return "mth";
            }
            return "";
        }
        if (this.durationUnit === "day") {
            if (this.paymentPeriod === null && this.durationUnit === 7) {
                return "wk";
            }
            if (this.paymentPeriod === 1) {
                return "day";
            }
            return "";
        }
        if (this.paymentPeriod === null && this.durationUnit === 24) {
            return "day";
        }
        if (this.paymentPeriod === 1) {
            return "hr";
        }
        return "";
    }
}

export default class SubscriptionCostResponse {
    constructor(data) {
        this.plans = data.plans.map(plan => new SubscriptionPlan(plan));
    }

    static default() {
        return new SubscriptionCostResponse({
            plans: []
        });
    }

    static async get(token) {
        const response = await axios.get(`${API_URL}/company/plans/subscriptions`, {
            headers: {
                Authorization: "Bearer " + token
            }
        });
        return new SubscriptionCostResponse(response.data);
    }

    /**
     * @returns {SubscriptionPlan}
     */
    primaryPlan() {
        return this.plans[0];
    }

    findPlanByName(name) {
        return this.plans.find(plan => plan.name === name);
    }
}
